'use client';

import type React from 'react';
import Image from 'next/image';

import { urlFor } from '@skybeam/shared-sanity';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import { utils } from '../../../_components/utils';
import type {
  ResponsivePadding,
  ResponsiveValue,
  SanityImage,
} from '../../../_components/utils/types';

export type StatsWithImageSectionProps = {
  backgroundColor?: string;
  containerPadding?: ResponsivePadding;
  contentMaxWidth?: ResponsiveValue;
  description?: string;
  heading: string;
  image: SanityImage;
  stats: Array<{ label: string; value: string }>;
  textContainerMaxWidth?: ResponsiveValue;
};

export const StatsWithImageSection = ({
  backgroundColor = '#fafaf5',
  containerPadding,
  contentMaxWidth,
  description,
  heading,
  image,
  stats,
  textContainerMaxWidth,
}: StatsWithImageSectionProps) => {
  const imageUrl = image?.asset && urlFor(image.asset).width(1200).url();
  const containerPaddingStyle =
    utils.responsiveUtils.getResponsivePaddingStyleTag(containerPadding);
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );
  const textContainerMaxWidthStyle =
    utils.responsiveUtils.getResponsiveValueStyleTag(
      textContainerMaxWidth,
      'max-width',
    );
  const styleTags = [
    containerPaddingStyle.styleTag,
    contentMaxWidthStyle.styleTag,
    textContainerMaxWidthStyle.styleTag,
  ].filter((tag): tag is React.ReactElement => tag !== null);

  return (
    <>
      {styleTags}
      <div
        className={cn(
          'tablet:py-20 desktop:py-24 flex justify-center px-6 py-12',
          containerPaddingStyle.className,
        )}
        style={{ backgroundColor }}
      >
        <div
          className={cn(
            'tablet:gap-y-12 desktop:flex-row desktop:gap-x-12 flex w-full flex-col items-center gap-y-6',
            contentMaxWidthStyle.className,
          )}
        >
          <div
            className={cn(
              'flex flex-col gap-y-6',
              textContainerMaxWidthStyle.className,
            )}
          >
            <h2 className="heading-marketing-2 desktop:text-left text-center font-light">
              {heading}
            </h2>
            {description && (
              <div className="tablet:text-lg desktop:text-left text-center text-base text-amber-600">
                {description}
              </div>
            )}
            <div className="tablet:justify-center tablet:gap-x-6 desktop:justify-start flex gap-x-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="tablet:w-auto flex w-full flex-col items-center gap-y-2"
                >
                  <div className="font-gelica tablet:text-[64px] laptop:text-[72px] desktop:text-[80px] text-5xl leading-[0.9] font-light text-blue-700">
                    {stat.value}
                  </div>
                  <div className="tablet:text-lg text-center text-xs">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full">
            {imageUrl && (
              <Image
                className="tablet:rounded-2xl w-full rounded-lg"
                src={imageUrl}
                alt={image.alt ?? ''}
                width={2596}
                height={1867}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
